import Alert from '@pelckmans/elementis/components/alert';
import { Cell } from '@pelckmans/elementis/components/table';
import Details from '@pelckmans/elementis/icons/details';
import { ReactNode, RefObject, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Medialink, validateAndGetLinkFromMedialink } from '../../../../../api/medialink';
import { Module } from '../../../../../api/module';
import { isLti, UserMaterial, validateAndGetLinkFromUserMaterial } from '../../../../../api/user-material';
import { ANALYTICS_EVENT_SUBLOCATIONS, ANALYTICS_EVENT_TYPES } from '../../../../../constants/analyticsEvents';
import ModalContext from '../../../../../contexts/ModalContext';
import useAnalytics from '../../../../../hooks/useAnalytics';
import usePlatformName from '../../../../../hooks/usePlatformName';
import LtiModal from '../../../../../shared-components/elementis/components/lti-modal';
import { buildBookWidgetUrl } from '../../../../../shared-components/elementis/util/buildBookWidgetsUrl';
import HorizontalScrollContextProps from '../../../../../shared-components/table/context/HorizontalScrollContext';
import { getResultPercentages } from '../../../../../utils/results';
import { TOKEN_ACCESSOR } from '../../../../../utils/tokenAccessor';
import useMedialinks from '../../../toc/hooks/medialink/useMedialinks';
import useUserMaterial from '../../../toc/hooks/user-material/useUserMaterial';
import { getSessions, Session } from '../../api/history';
import { ScorableItem } from '../../api/scorable-items';
import AbsoluteScoreCell from '../table/cells/AbsoluteScoreCell';
import DateCell from '../table/cells/DateCell';
import PercentageCell from '../table/cells/PercentageCell';
import TimeCell from '../table/cells/TimeCell';

const weekdayFormatter = new Intl.DateTimeFormat('nl-BE', {
  weekday: 'short',
});
const dayMonthFormatter = new Intl.DateTimeFormat('nl-BE', {
  day: '2-digit',
  month: '2-digit',
});

function FromToCell({ from, to }: { from: string; to: string }) {
  const { t } = useTranslation();
  const fromDate = new Date(from);
  const fromDateWeekDay = weekdayFormatter.format(fromDate);
  const fromDateDate = dayMonthFormatter.format(fromDate);

  const toDate = new Date(to);
  const toDateWeekDay = weekdayFormatter.format(toDate);
  const toDateDate = dayMonthFormatter.format(toDate);

  return (
    <Cell pillGroupProps={{ 'aria-label': undefined, role: 'group' }} minWidth="md">
      <Cell.Pill>
        {t(`${BASE_PATH}.TABLE_VALUES.FROM_TO`, {
          from: `${fromDateWeekDay} ${fromDateDate}`,
          to: `${toDateWeekDay} ${toDateDate}`,
        })}
      </Cell.Pill>
    </Cell>
  );
}

const BASE_PATH = 'PAGES.RESULTS.SESSION_HISTORY_MODAL';

interface Props {
  sessionsData: Awaited<ReturnType<typeof getSessions>>;
  scorableItem: ScorableItem;
  isAssignment: boolean;
  userMaterial?: UserMaterial;
  materialModule: Module;
  headerTags: ReactNode;
  medialink?: Medialink;
  module: Module;
  tableRef: RefObject<HTMLTableElement>;
}

export default function HistoryTable({
  sessionsData,
  scorableItem,
  isAssignment,
  userMaterial,
  materialModule,
  headerTags,
  medialink,
  tableRef,
}: Props) {
  const {
    REACT_APP_LTI_AUTH_URL,
    REACT_APP_BOOKWIDGETS_REVIEW_URL,
    REACT_APP_P_STUDIO_LANGUAGE,
    REACT_APP_P_STUDIO_GROUP,
  } = process.env;

  const { t } = useTranslation();
  const platformName = usePlatformName();
  const { openReview } = useMedialinks();
  const { capture } = useAnalytics();
  const { openMaterial } = useUserMaterial();

  const { showModal, closeModal } = useContext(ModalContext);
  const { horizontalScroll } = useContext(HorizontalScrollContextProps);

  if (sessionsData.sessions.length === 0) {
    return (
      <Alert prominence="warning">
        {t(`${BASE_PATH}.TABLE_VALUES.NO_SESSIONS`, { memberName: sessionsData?.memberName })}
      </Alert>
    );
  }

  async function onMaterialAccessed(session: Session) {
    if (!materialModule) return;

    if (medialink && scorableItem.type === 'medialink') {
      const node = medialink.hierarchy[medialink.hierarchy.length - 1];

      const modal = await openReview({
        medialink,
        selectedModule: materialModule,
        sessionId: session.session!,
        session: session,
        sessionStudentName: sessionsData.memberName,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
        nodeWithoutDescription: node,
      });

      if (!modal) return;

      return showModal(modal);
    }

    if (!userMaterial) return;
    if (isLti(userMaterial) && 'userMaxScore' in userMaterial) {
      capture({
        eventType: ANALYTICS_EVENT_TYPES.USER_MATERIAL_ACCESSED,
        subLocationId: ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
        objectId: scorableItem.id,
      });

      const ltiUserMaterial = userMaterial.assignment
        ? { ...userMaterial, assignmentId: userMaterial.assignment.id }
        : userMaterial;

      const buildOptions: Parameters<typeof buildBookWidgetUrl>[0] = {
        platformName,
        ltiUserMaterial,
        ltipsUrl: REACT_APP_LTI_AUTH_URL,
        module: materialModule,
        studioLanguage: REACT_APP_P_STUDIO_LANGUAGE,
        reviewUrl: REACT_APP_BOOKWIDGETS_REVIEW_URL,
        audience: userMaterial.assignment,
      };

      const MODAL_ID = 'lti-modal';

      return showModal(
        <LtiModal
          id={MODAL_ID}
          aria-label=""
          headerProps={{
            title: t('PAGES.RESULTS.MODALS.LTI.TITLE', {
              materialName: userMaterial.name,
            }),
            titleTooltip: t('PAGES.RESULTS.MODALS.LTI.TITLE', {
              materialName: userMaterial.name,
            }),
            tags: headerTags,
          }}
          loginUrl={buildBookWidgetUrl(buildOptions)}
          onClose={() => closeModal(MODAL_ID)}
          studioGroup={REACT_APP_P_STUDIO_GROUP}
          tokenAccessor={TOKEN_ACCESSOR}
          loadingText={{
            title: t('EXERCISE_MANAGER.BOOKWIDGET_LOADING.TITLE'),
            p1: t('EXERCISE_MANAGER.BOOKWIDGET_LOADING.P1'),
            p2: t('EXERCISE_MANAGER.BOOKWIDGET_LOADING.P2'),
          }}
        />,
      );
    }

    const modal = await openMaterial(
      userMaterial,
      userMaterial.hierarchy!,
      undefined,
      ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_RESULTS,
    );

    if (!modal) return;

    return showModal(modal);
  }

  const link =
    (userMaterial && validateAndGetLinkFromUserMaterial(userMaterial)) ||
    (medialink && validateAndGetLinkFromMedialink(medialink)) ||
    undefined;

  return (
    <table className="history-table" ref={tableRef}>
      <thead>
        <tr>
          {isAssignment && <Cell.HeaderText title={t(`${BASE_PATH}.TABLE_HEADER.SHARED`)} minWidth="md" sticky />}
          <Cell.HeaderText title={t(`${BASE_PATH}.TABLE_HEADER.DATE_SUBMITTED`)} minWidth="sm" maxWidth="sm" sticky />
          <Cell.HeaderText title={t(`${BASE_PATH}.TABLE_HEADER.RESULTS`)} minWidth="xs" maxWidth="xs" sticky />
          <Cell.HeaderText title={t(`${BASE_PATH}.TABLE_HEADER.PERCENTAGE`)} minWidth="xs" maxWidth="xs" sticky />
          <Cell.HeaderText title={t(`${BASE_PATH}.TABLE_HEADER.SCORE`)} minWidth="sm" maxWidth="sm" sticky />
          <Cell.HeaderText title={t(`${BASE_PATH}.TABLE_HEADER.TIME_SPENT`)} minWidth="sm" maxWidth="sm" sticky />
          <Cell.HeaderEmpty className="actions-cell" sticky />
        </tr>
      </thead>
      <tbody>
        {sessionsData?.sessions.map((session, i) => (
          <tr key={`${session.session}-${i}`}>
            {isAssignment && session.assignment && (
              <FromToCell from={session.assignment.from} to={session.assignment.to} />
            )}
            <DateCell
              completedBySystem={session.completedBySystem ?? false}
              isZeroScore={session.isZeroScore ?? false}
              achievedAt={session.achievedAt}
            />
            <Cell minWidth="xs">
              <Cell.Results
                {...getResultPercentages(session.score, session.lti?.gradingProgress === 'PendingManual')}
                quantifiable={!scorableItem.unachievable && session.score?.achieved !== -1}
                status={session.status}
                showTooltip
                variant="donut"
              />
            </Cell>
            <PercentageCell
              achieved={session.score.achieved}
              maximum={session.score.maximum}
              needsGrading={session.lti?.gradingProgress === 'PendingManual'}
            />
            <AbsoluteScoreCell
              scorableItem={scorableItem}
              score={session.score}
              needsGrading={session.lti?.gradingProgress === 'PendingManual'}
            />
            <TimeCell duration={session.duration} />
            <Cell
              buttonGroupProps={{
                'aria-label': t(`${BASE_PATH}.TABLE_VALUES.DETAILS`),
                role: 'group',
                className: 'button-group',
              }}
              className="actions-cell"
              sticky={horizontalScroll}
            >
              {link ? (
                <Cell.Button
                  as="link"
                  href={link}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => onMaterialAccessed(session)}
                  startIcon={<Details />}
                  prominence="neutral"
                  aria-label={t(`${BASE_PATH}.TABLE_VALUES.DETAILS_BUTTON`)}
                />
              ) : (
                <Cell.Button
                  onClick={() => onMaterialAccessed(session)}
                  startIcon={<Details />}
                  prominence="neutral"
                  aria-label={t(`${BASE_PATH}.TABLE_VALUES.DETAILS_BUTTON`)}
                />
              )}
            </Cell>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
